import { API_URL_All } from "../api";

let cachedConfig = null;
let inflight = null;

export function clearTradeChatConfigCache() {
  cachedConfig = null;
  inflight = null;
}

/**
 * Version cache-bust dạng v=ddHHMM (ngày + giờ + phút, local time).
 * @returns {string}
 */
export function getTradeScriptVersion() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const HH = String(now.getHours()).padStart(2, "0");
  const MM = String(now.getMinutes()).padStart(2, "0");
  return `${dd}${HH}${MM}`;
}

/**
 * Gắn/ghi đè query v=ddHHMM vào URL file JS Trade.
 * @param {string} url
 * @returns {string}
 */
export function withTradeScriptVersion(url) {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  const v = getTradeScriptVersion();
  if (/[?&]v=/.test(raw)) {
    return raw.replace(/([?&])v=[^&#]*/, `$1v=${v}`);
  }
  const sep = raw.includes("?") ? "&" : "?";
  return `${raw}${sep}v=${v}`;
}

/** Đường dẫn token nội bộ — trade_portal.js fetch path này; client mode mock như portal.html. */
export const TRADE_PORTAL_TOKEN_URL = "/api/admin/trade-portal-session";

/** Lấy câu lỗi mà Trade Portal trả (error / message / title), không bọc thêm. */
export function describePortalError(bodyText, status) {
  const raw = String(bodyText || "").trim();
  if (!raw) {
    return status ? `Trade portal HTTP ${status}` : "";
  }
  try {
    const data = JSON.parse(raw);
    if (typeof data?.error === "string" && data.error.trim()) {
      return data.error.trim();
    }
    if (typeof data?.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
    if (typeof data?.title === "string" && data.title.trim()) {
      const parts = [data.title.trim()];
      if (data.errors && typeof data.errors === "object") {
        for (const value of Object.values(data.errors)) {
          if (Array.isArray(value)) {
            for (const item of value) {
              if (item) parts.push(String(item));
            }
          }
        }
      }
      return parts.join(" ");
    }
  } catch {
    // body không phải JSON
  }
  return raw.length > 500 ? `${raw.slice(0, 500)}...` : raw;
}

function showPortalError(message) {
  if (!message) return;
  const root = document.getElementById("trade_portal_root");
  if (root) {
    root.textContent = message;
  }
  console.error(message);
}

/** Endpoint nội bộ cấp session portal (Bearer JWT ZTO) — dùng cho server security. */
export function resolveTradePortalSessionUrl(portalSessionPath) {
  const base = String(API_URL_All || "").replace(/\/$/, "");
  const path = String(portalSessionPath || TRADE_PORTAL_TOKEN_URL).trim();
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

const buildStaffSessionPayload = (profile = {}) => {
  const payload = {
    externalUserId: String(profile.id || ""),
    displayName: String(
      profile.name || profile.username || profile.email || ""
    ).trim(),
    email: profile.email != null ? String(profile.email) : "",
  };
  if (profile.phoneNumber) {
    payload.phone = String(profile.phoneNumber).trim();
  }
  const picture = profile.picture != null ? String(profile.picture).trim() : "";
  if (/^https?:\/\//i.test(picture)) {
    payload.avatarUrl = picture;
  }
  return payload;
};

/**
 * Chế độ bảo mật client — giống portal.html / portal_js.js:
 * - Lấy publicKey + portalDomain từ server
 * - Mock fetch tại tokenUrl nội bộ, POST sang Double Boss /api/portal/session
 * - Load trade_portal.js từ portalDomain với data-token-url tương đối
 */
export function installTradePortalClientIntegration({
  publicKey,
  portalDomain,
  tokenUrl = TRADE_PORTAL_TOKEN_URL,
}) {
  if (typeof window === "undefined" || window.__tradePortalClientIntegration) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  const portalBase = String(portalDomain || "").replace(/\/$/, "");
  const sessionUrl = `${portalBase}/api/portal/session`;
  const apiBase = String(API_URL_All || "").replace(/\/$/, "");
  const normalizedTokenUrl = String(tokenUrl || TRADE_PORTAL_TOKEN_URL).trim();

  window.fetch = async (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    if (!url.includes(normalizedTokenUrl)) {
      return originalFetch(input, init);
    }

    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) {
      throw new Error("Chưa đăng nhập — không lấy được trade portal session.");
    }

    const profileRes = await originalFetch(`${apiBase}/api/users/getProfile`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const profileBody = await profileRes.json().catch(() => null);
    const staffPayload = buildStaffSessionPayload(profileBody?.data || {});

    const dbResponse = await originalFetch(sessionUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Trade-Public-Key": publicKey,
      },
      body: JSON.stringify(staffPayload),
    });

    const dbData = await dbResponse.json().catch(() => ({}));
    if (!dbResponse.ok) {
      showPortalError(describePortalError(JSON.stringify(dbData), dbResponse.status));
    }
    return new Response(JSON.stringify(dbData), {
      status: dbResponse.status,
      headers: { "Content-Type": "application/json" },
    });
  };

  window.__tradePortalClientIntegration = true;
}

/**
 * Server security: session qua proxy ZTO, Bearer JWT.
 */
export function ensureTradePortalAuthFetch() {
  if (typeof window === "undefined" || window.__tradePortalAuthFetch) {
    return;
  }
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input && typeof input.url === "string"
          ? input.url
          : "";
    if (!url.includes(TRADE_PORTAL_TOKEN_URL)) {
      return originalFetch(input, init);
    }
    const token =
      typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) {
      return originalFetch(input, init);
    }
    const headers = new Headers(
      (init && init.headers) ||
        (typeof Request !== "undefined" && input instanceof Request
          ? input.headers
          : undefined)
    );
    if (!headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const response = await originalFetch(input, { ...(init || {}), headers });
    if (!response.ok) {
      const text = await response.clone().text().catch(() => "");
      showPortalError(describePortalError(text, response.status));
    }
    return response;
  };
  window.__tradePortalAuthFetch = true;
}

/**
 * Lấy publicKey + portalDomain từ server (chế độ bảo mật client).
 * @returns {Promise<{ publicKey: string, portalDomain: string, portalScriptUrl: string, tokenUrl: string }>}
 */
export async function fetchTradePortalClientConfig() {
  const base = String(API_URL_All || "").replace(/\/$/, "");
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
  if (!token) {
    throw new Error("Chưa đăng nhập — không lấy được cấu hình portal client.");
  }

  const res = await fetch(`${base}/api/admin/trade-portal-client-config`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(
      body?.message || `Không lấy được cấu hình portal client (HTTP ${res.status}).`
    );
  }

  const data = body?.data || body || {};
  const publicKey = String(data.publicKey || "").trim();
  const portalDomain = String(data.portalDomain || "").replace(/\/$/, "");
  if (!publicKey) {
    throw new Error("Server chưa cấu hình Portal key.");
  }
  if (!portalDomain) {
    throw new Error("Server chưa trả portalDomain.");
  }

  return {
    publicKey,
    portalDomain,
    portalScriptUrl:
      String(data.portalScriptUrl || "").trim() ||
      `${portalDomain}/js/trade_portal.js`,
    tokenUrl: String(data.tokenUrl || TRADE_PORTAL_TOKEN_URL).trim(),
  };
}

/**
 * Lấy cấu hình Trade chat từ server (Public key cho chat khách, host portal).
 * @returns {Promise<{ publicKey: string, tradeHost: string, portalSecurityMode: string, clientScriptUrl: string, portalScriptUrl: string, portalSessionPath: string }>}
 */
export async function fetchTradeChatConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }
  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    const base = String(API_URL_All || "").replace(/\/$/, "");
    const res = await fetch(`${base}/api/trade-chat/config`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      const serverMessage = String(body?.message || "").trim();
      throw new Error(
        serverMessage || `Không lấy được cấu hình Trade chat (HTTP ${res.status}).`
      );
    }
    const data = body?.data || body || {};
    const publicKey = String(data.publicKey || "").trim();
    const tradeHost = String(data.tradeHost || "").replace(/\/$/, "");
    if (!tradeHost) {
      throw new Error("Server chưa cấu hình trade.chat.host.");
    }
    const portalSecurityMode =
      String(data.portalSecurityMode || "server").trim().toLowerCase() === "client"
        ? "client"
        : "server";
    cachedConfig = {
      publicKey,
      tradeHost,
      portalSecurityMode,
      clientScriptUrl:
        String(data.clientScriptUrl || "").trim() ||
        `${tradeHost}/js/trade_web_client.js`,
      portalScriptUrl:
        String(data.portalScriptUrl || "").trim() ||
        `${tradeHost}/js/trade_portal.js`,
      portalSessionPath:
        String(data.portalSessionPath || "").trim() || TRADE_PORTAL_TOKEN_URL,
    };
    return cachedConfig;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
